/**
 * Protected Route
 * Guards routes that require authentication
 * Sends users to the appropriate login page for the requested area
 */

import { Navigate, useLocation } from 'react-router-dom';
import { useAppSelector } from '@/store/hooks';
import { selectIsAuthenticated, selectAuthLoading, selectAuthInitialized } from '@/store/slices';
import { TokenManager } from '@/shared/api';
import { PATHS } from './paths';

interface ProtectedRouteProps {
    children: React.ReactNode;
    requiredRole?: 'admin' | 'user' | 'agent';
}

export function ProtectedRoute({ children, requiredRole }: ProtectedRouteProps) {
    const isAuthenticated = useAppSelector(selectIsAuthenticated);
    const isLoading = useAppSelector(selectAuthLoading);
    const isInitialized = useAppSelector(selectAuthInitialized);
    const user = useAppSelector((state) => state.auth.user);
    const location = useLocation();

    // Check if there's a token
    const hasToken = TokenManager.hasToken();

    // Show loading while:
    // 1. Auth check is in progress (isLoading)
    // 2. We have a token but auth hasn't been initialized yet (waiting for getCurrentUser)
    if (isLoading || (hasToken && !isInitialized)) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
            </div>
        );
    }

    const loginPath = requiredRole === 'admin' ? PATHS.ADMIN.LOGIN : PATHS.AUTH.LOGIN;

    // Not authenticated - redirect to the relevant login page
    if (!isAuthenticated) {
        return <Navigate to={loginPath} state={{ from: location }} replace />;
    }

    // Check role if required
    if (requiredRole && user?.role !== requiredRole) {
        // User doesn't have required role - redirect to home
        return <Navigate to={PATHS.HOME} replace />;
    }

    return <>{children}</>;
}

export default ProtectedRoute;
